import React, { useEffect } from 'react';
import { Modal, Button, message, Form } from 'antd';
import dayjs from 'dayjs';
import {
  useCreateRouteExpenseMutation,
  useUpdateRouteExpenseMutation,
  type RouteExpense,
} from '../../store/api/financeApi';
import RouteExpenseForm from '../../features/finance/components/shared/RouteExpenseForm';
import {
  mapRouteExpenseToFormValues,
  mapFormValuesToCreatePayload,
  mapFormValuesToUpdatePayload,
  type RouteExpenseFormValues
} from '../../features/finance/domain/routeExpenseFormValues';

interface CreateRouteExpenseModalProps {
  visible: boolean;
  routeId: number;
  onClose: () => void;
  onSuccess?: () => void;
  editExpense?: RouteExpense | null;
}

const CreateRouteExpenseModal: React.FC<CreateRouteExpenseModalProps> = ({
  visible,
  routeId,
  onClose,
  onSuccess,
  editExpense = null,
}) => {
  const [form] = Form.useForm<RouteExpenseFormValues>();
  const [createExpense, { isLoading }] = useCreateRouteExpenseMutation();
  const [updateExpense, { isLoading: isUpdating }] = useUpdateRouteExpenseMutation();
  const isEditMode = Boolean(editExpense?.id);

  useEffect(() => {
    if (visible) {
      form.resetFields();
      if (editExpense) {
        form.setFieldsValue(mapRouteExpenseToFormValues(editExpense));
      } else {
        form.setFieldsValue({
          type: 'OTHER',
          vatRate: 20,
          expenseDate: dayjs(),
          description: '',
          comment: '',
          amountWithVAT: null,
          invoiceId: null
        });
      }
    }
  }, [visible, form, editExpense]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = mapFormValuesToCreatePayload(values);
      if (isEditMode && editExpense?.id) {
        await updateExpense({
          routeId,
          expenseId: editExpense.id,
          data: mapFormValuesToUpdatePayload(values),
        }).unwrap();
        message.success('Расход обновлен');
      } else {
        await createExpense({
          routeId,
          data: payload,
        }).unwrap();
        message.success('Расход добавлен — отправлен бухгалтеру на подтверждение');
      }
      onSuccess?.();
      onClose();
    } catch (err: any) {
      message.error(err?.data?.error || err?.message || (isEditMode ? 'Ошибка обновления расхода' : 'Ошибка создания расхода'));
    }
  };

  return (
    <Modal
      title={isEditMode ? 'Редактировать расход' : 'Добавить расход'}
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Отмена
        </Button>,
        <Button key="submit" type="primary" loading={isLoading || isUpdating} onClick={handleSubmit}>
          Сохранить
        </Button>,
      ]}
      destroyOnClose
    >
      <RouteExpenseForm useAntForm form={form} mode={isEditMode ? 'edit' : 'create'} showComment />
    </Modal>
  );
};

export default CreateRouteExpenseModal;
